import {useCallback, useContext, useMemo, useState} from 'react';
import {NavigationContext, NavigationRouteContext} from '@react-navigation/native';

/**
 * Состояние экрана, которое живёт В АДРЕСЕ, а не в `useState`.
 *
 * ## Что это чинит
 *
 * Кабинет открывается в браузере, и до сих пор каждый фильтр, период, страница и
 * сортировка были локальным состоянием компонента. Отсюда три вещи, которые люди
 * читают как поломку:
 *
 *   1. **F5 стирает работу.** Настроил в журнале период, отдел, терминал и
 *      «показать опоздания» — обновил страницу и настраиваешь заново.
 *   2. **«Назад» приводит не туда, откуда ушёл.** Провалился из отчёта в карточку
 *      сотрудника, вернулся — отчёт снова за месяц по умолчанию.
 *   3. **Нечем поделиться.** «Посмотри, кто опаздывал в цехе на прошлой неделе» —
 *      это ссылка в любой веб-панели и пересказ из шести шагов здесь.
 *
 * Адрес — единственное место, которое переживает перезагрузку, попадает в историю
 * браузера и копируется в мессенджер. Поэтому состояние ВОПРОСА («что я смотрю»)
 * живёт там, а состояние ЖЕСТА («открыт ли этот раскрыватель», «набран ли черновик»)
 * остаётся в компоненте — ему в ссылке делать нечего.
 *
 * ## Как это работает
 *
 * `linking.ts` уже даёт каждому экрану путь, а react-navigation дописывает в строку
 * запроса те параметры маршрута, которые в путь не вошли. Значит, вся механика — это
 * `route.params` как источник правды и `navigation.setParams` как запись; на нативе
 * адреса нет, и там хук просто хранит то же самое в параметрах маршрута, что тоже
 * правильно: экран, перемонтированный из стека, приходит со своим вопросом.
 *
 * Значение по умолчанию в адрес НЕ пишется (`format` возвращает `undefined`), иначе
 * ссылка на «Посещаемость» выглядела бы как `?period=m:2026-09&departments=&days=0`
 * и половина её была бы шумом.
 *
 * Вне навигатора хук ведёт себя как обычный `useState` — см. `useParamState`.
 */
export type ParamCodec<T> = {
  /** Разобрать то, что пришло из адреса. Обязан пережить мусор — см. `parsePeriodKey`. */
  parse: (raw: string) => T;
  /** Свернуть в строку, либо `undefined` — «это значение по умолчанию, в адрес не писать». */
  format: (value: T) => string | undefined;
};

/** Строка: пустая считается значением по умолчанию. */
export const stringParam: ParamCodec<string> = {
  parse: raw => raw,
  format: value => (value ? value : undefined),
};

/** Флажок. В адресе выглядит как `days=1`. */
export const boolParam: ParamCodec<boolean> = {
  parse: raw => raw === '1' || raw === 'true',
  format: value => (value ? '1' : undefined),
};

/** Целое с собственным «пустым» значением — номер страницы, размер страницы. */
export function intParam(fallback: number): ParamCodec<number> {
  return {
    parse: raw => {
      const n = Number.parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : fallback;
    },
    format: value => (value === fallback ? undefined : String(value)),
  };
}

/** Набор идентификаторов через запятую — фильтр по нескольким отделам. */
export const listParam: ParamCodec<string[]> = {
  parse: raw => raw.split(',').filter(Boolean),
  format: value => (value.length > 0 ? value.join(',') : undefined),
};

/** Значение из закрытого набора; всё незнакомое — обратно к умолчанию. */
export function enumParam<T extends string>(
  options: readonly T[],
  fallback: T,
): ParamCodec<T> {
  return {
    parse: raw => (options.includes(raw as T) ? (raw as T) : fallback),
    format: value => (value === fallback ? undefined : value),
  };
}

/**
 * Один параметр экрана, прочитанный из адреса и туда же записываемый.
 *
 * Читается как `useState`, и это намеренно: место хранения — единственное, что
 * меняется на вызывающей стороне.
 *
 * Вне навигатора — в тесте, который рендерит один список сам по себе, — падать было бы
 * неправильно: компонент от этого не перестаёт быть компонентом, а хук по контракту
 * «как `useState`, но в адресе» честно вырождается в первую половину этой фразы.
 * Контексты читаются напрямую, а не через `useNavigation`/`useRoute`: те бросают
 * исключение вместо того, чтобы вернуть `undefined`.
 */
export function useParamState<T>(
  key: string,
  fallback: T,
  codec: ParamCodec<T>,
): [T, (next: T) => void] {
  const navigation = useContext(NavigationContext);
  const route = useContext(NavigationRouteContext);
  const [own, setOwn] = useState<T>(fallback);

  const raw = (route?.params as Record<string, unknown> | undefined)?.[key];

  const parsed = useMemo(
    () => (typeof raw === 'string' ? codec.parse(raw) : fallback),
    // `codec` и `fallback` объявляются модульно или литералом на месте вызова;
    // сторожить их значило бы пересчитывать разбор на каждый рендер впустую.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [raw],
  );

  const set = useCallback(
    (next: T) => {
      if (!navigation) {
        setOwn(next);
        return;
      }
      // `setParams` СЛИВАЕТ, а не заменяет: два фильтра, переставленные в одном
      // кадре, не затирают друг друга.
      navigation.setParams({[key]: codec.format(next)} as never);
    },
    // Тот же довод, что и выше: кодек стабилен по построению.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigation, key],
  );

  return [navigation ? parsed : own, set];
}
